/**
 * Q8 — Tile-load progress chip for the 3D toolbar.
 *
 * States:
 *  - hidden (idle): no load activity for > 1 s
 *  - loading: active tile batch / streaming
 *  - done: "Ready" with green dot for 1.5 s, then fades out
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { tokens } from '@/design';
import type { Giro3DAdapter, TileLoadProgress } from '@/engines/giro3d/Giro3DAdapter';

type ChipDisplay = 'hidden' | 'loading' | 'done';

export interface TileLoadChipProps {
  readonly adapter: Giro3DAdapter | null;
}

const DONE_MS = 1500;

export function TileLoadChip({ adapter }: TileLoadChipProps) {
  const [display, setDisplay] = useState<ChipDisplay>('hidden');
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const idleTimer = useRef<number | null>(null);
  const doneTimer = useRef<number | null>(null);

  const clearIdleTimer = () => {
    if (idleTimer.current != null) {
      window.clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  };

  const clearDoneTimer = () => {
    if (doneTimer.current != null) {
      window.clearTimeout(doneTimer.current);
      doneTimer.current = null;
    }
  };

  const applyProgress = (progress: TileLoadProgress) => {
    setLoaded(progress.loaded);
    setTotal(progress.total);

    if (progress.phase === 'done') {
      clearIdleTimer();
      setDisplay('done');
      clearDoneTimer();
      doneTimer.current = window.setTimeout(() => {
        setDisplay('hidden');
      }, DONE_MS);
      return;
    }

    clearDoneTimer();
    setDisplay('loading');
    clearIdleTimer();
  };

  useEffect(() => {
    if (!adapter) {
      setDisplay('hidden');
      return undefined;
    }

    applyProgress(adapter.getTileLoadProgress());

    const unsubscribe = adapter.subscribeTileLoadProgress((progress) => {
      applyProgress(progress);
    });

    return () => {
      unsubscribe();
      clearIdleTimer();
      clearDoneTimer();
    };
  }, [adapter]);

  if (display === 'hidden') return null;

  const label =
    display === 'done'
      ? 'Ready'
      : total > 0
        ? `Loading ${loaded}/${total} tiles`
        : 'Loading…';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="tile-load-chip"
      data-state={display}
      style={chipStyle(display === 'done')}
    >
      {display === 'done' ? (
        <span aria-hidden style={readyDotStyle} />
      ) : null}
      <span>{label}</span>
    </div>
  );
}

function chipStyle(done: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    borderRadius: tokens.radius.pill,
    background: tokens.color.surface[3],
    color: tokens.color.ink.primary,
    fontSize: tokens.font.role.label.size,
    lineHeight: tokens.font.lineHeight.tight,
    whiteSpace: 'nowrap',
    opacity: done ? 1 : 0.95,
    transition: `opacity ${tokens.motion.duration.fast}ms ease`,
  };
}

const readyDotStyle: CSSProperties = {
  width: tokens.space[2],
  height: tokens.space[2],
  borderRadius: '50%',
  background: tokens.color.semantic.success,
  flexShrink: 0,
};
